import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SUBJECTS_FALLBACK } from "../lib/subjects";
import { TeacherCard, TeacherCardSkeleton } from "../components/TeacherCard";
import ParentCoursesPanel, { RatingBlockModal } from "../components/ParentCoursesPanel";
import NotificationBanner from "../components/NotificationBanner";
import { CancelButton, PunchInButton, DemoCancelButton, StartSessionButton, ReportSessionButton } from "../components/SessionActions";
import { WalletPanel } from "../components/WalletPanel";
import { SubscriptionPanel } from "../components/SubscriptionPanel";
import { toast } from "sonner";
import {
  User,
  Search as SearchIcon,
  Users,
  GraduationCap,
  Star,
  Sparkles,
  X,
  CheckCircle2,
  Wallet,
  Crown,
} from "lucide-react";

const TABS = [
  { key: "profile", label: "My Profile", icon: User },
  { key: "subscription", label: "Subscription", icon: Crown },
  { key: "wallet", label: "Wallet", icon: Wallet },
  { key: "search", label: "Search", icon: SearchIcon },
  { key: "browse", label: "Browse Mentors", icon: Users },
  { key: "courses", label: "My Sessions", icon: GraduationCap },
  { key: "completed", label: "Completed Sessions", icon: CheckCircle2 },
  { key: "demo", label: "Book Demo Class", icon: Sparkles },
];

const GRADES = [
  "Pre-school", "Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4",
  "Grade 5", "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10",
  "Grade 11", "Grade 12", "Other",
];

const BOARDS = ["CBSE", "ICSE", "IB", "IGCSE", "State Board", "Other"];

function flattenSubjects(subjects) {
  const all = [];
  for (const cat of Object.values(subjects)) {
    for (const items of Object.values(cat)) all.push(...items);
  }
  return Array.from(new Set(all)).sort();
}

// ============ PROFILE TAB ============
function ProfileTab({ user }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", grade: "", school: "", board: "", location: "", phone: "", hobbies: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/parent/profile");
      setProfile(data);
      // Auto-create a single "self" explorer profile if none exists yet
      const children = data?.children || [];
      if (children.length === 0) {
        await api.post("/parent/profile/children", {
          name: user?.name || "Explorer",
          grade: "",
          school: "",
          board: "",
          location: "",
          hobbies: "",
        });
        const refreshed = await api.get("/parent/profile");
        setProfile(refreshed.data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!editId) return;
    try {
      await api.put(`/parent/profile/children/${editId}`, form);
      toast.success("Profile updated");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  // Sync form state with the single explorer profile after load
  useEffect(() => {
    const c = profile?.children?.[0];
    if (c && c.id !== editId) {
      setEditId(c.id);
      setForm({
        name: c.name || "",
        grade: c.grade || "",
        school: c.school || "",
        board: c.board || "",
        location: c.location || "",
        phone: c.phone || "",
        hobbies: c.hobbies || "",
      });
    }
  }, [profile, editId]);

  const startEdit = () => {
    const c = profile?.children?.[0];
    if (c) {
      setForm({
        name: c.name || "",
        grade: c.grade || "",
        school: c.school || "",
        board: c.board || "",
        location: c.location || "",
        phone: c.phone || "",
        hobbies: c.hobbies || "",
      });
    }
    setEditing(true);
  };

  const cancelEdit = () => {
    const c = profile?.children?.[0];
    if (c) {
      setForm({
        name: c.name || "",
        grade: c.grade || "",
        school: c.school || "",
        board: c.board || "",
        location: c.location || "",
        phone: c.phone || "",
        hobbies: c.hobbies || "",
      });
    }
    setEditing(false);
  };

  if (loading) return <p className="text-sm" style={{ color: "#13A699" }}>Loading…</p>;

  const explorer = profile?.children?.[0];

  return (
    <div data-testid="parent-profile-tab">
      <div className="bg-white rounded-3xl p-5 border border-black/5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-xl font-bold uppercase" style={{ color: "#13A699" }}>
              Explorer Profile
            </h2>
            <p className="text-xs mt-0.5" style={{ color: "#13A699AA" }}>{user?.name} · {user?.email}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!editing && (
              <button
                type="button"
                onClick={startEdit}
                className="btn-yellow text-xs px-4 py-2 inline-flex items-center gap-1"
                data-testid="edit-profile-btn"
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>

      {!editing ? (
        <div
          className="mt-4 bg-white rounded-3xl p-5 border border-black/5 space-y-2"
          data-testid="explorer-profile-view"
        >
          <h3 className="font-display font-bold uppercase text-sm" style={{ color: "#13A699" }}>
            Your Learning Profile
          </h3>
          <ProfileRow label="Name" value={explorer?.name} />
          <ProfileRow label="Grade / Level" value={explorer?.grade} />
          <ProfileRow label="School / Institution" value={explorer?.school} />
          <ProfileRow label="Board" value={explorer?.board} />
          <ProfileRow label="City / Location" value={explorer?.location} />
          <ProfileRow label="Phone" value={explorer?.phone} />
          <ProfileRow label="Hobbies / Interests" value={explorer?.hobbies} />
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="mt-4 bg-white rounded-3xl p-5 border border-black/5 space-y-3"
          data-testid="explorer-profile-form"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold uppercase text-sm" style={{ color: "#13A699" }}>
              Edit Learning Profile
            </h3>
            <button
              type="button"
              onClick={cancelEdit}
              className="text-xs font-bold px-3 py-1.5 rounded-full border border-[#13A699]"
              style={{ color: "#13A699" }}
              data-testid="edit-profile-cancel-btn"
            >
              Cancel
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                data-testid="child-name-input"
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Grade / Level</label>
              <select
                value={form.grade}
                onChange={(e) => setForm({ ...form, grade: e.target.value })}
                className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                data-testid="child-grade-input"
              >
                <option value="">Select grade / level</option>
                {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>School / Institution</label>
              <input
                value={form.school}
                onChange={(e) => setForm({ ...form, school: e.target.value })}
                className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                data-testid="child-school-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Board</label>
              <select
                value={form.board}
                onChange={(e) => setForm({ ...form, board: e.target.value })}
                className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                data-testid="child-board-input"
              >
                <option value="">Select board</option>
                {BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>City / Location</label>
              <input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Mumbai"
                className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                data-testid="child-location-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Phone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="e.g. +91 98xxx xxxxx"
                className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                data-testid="child-phone-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Hobbies / Interests</label>
              <input
                value={form.hobbies}
                onChange={(e) => setForm({ ...form, hobbies: e.target.value })}
                placeholder="e.g. chess, football, painting"
                className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                data-testid="child-hobbies-input"
              />
            </div>
          </div>
          <div className="pt-1">
            <button type="submit" className="btn-yellow text-sm px-4 py-2" data-testid="child-form-submit">
              Save Profile
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function ProfileRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm py-1.5 border-b border-black/5 last:border-0">
      <div className="text-xs font-bold uppercase" style={{ color: "#13A699AA" }}>{label}</div>
      <div className="col-span-2" style={{ color: "#13A699" }}>{value || <span className="opacity-60">—</span>}</div>
    </div>
  );
}


// ============ SEARCH TAB ============
function SearchTab({ subjectsTree }) {
  const allSubjects = useMemo(() => flattenSubjects(subjectsTree), [subjectsTree]);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState("");
  const [location, setLocation] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams();
      if (subject) params.set("subject", subject);
      if (location) params.set("location", location);
      const { data } = await api.get(`/teachers/browse?${params.toString()}`);
      // Apply text search and grade filter client-side (grade filter is informative — teachers don't have grade field, but we keep grade query for future)
      const ql = q.trim().toLowerCase();
      const filtered = (data || []).filter((t) => {
        if (!ql) return true;
        return (t.name || "").toLowerCase().includes(ql)
          || (t.bio || "").toLowerCase().includes(ql)
          || (t.subjects || []).some((s) => s.toLowerCase().includes(ql));
      });
      setResults(filtered);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setQ(""); setSubject(""); setGrade(""); setLocation(""); setResults([]); setSearched(false); };

  return (
    <div data-testid="parent-search-tab">
      <div className="bg-white rounded-3xl p-5 border border-black/5">
        <h2 className="font-display text-xl font-bold uppercase mb-3" style={{ color: "#13A699" }}>
          Search Mentors
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Keyword</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, subject, or bio…"
              className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
              data-testid="search-keyword-input"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Subject</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
              data-testid="search-subject-select"
            >
              <option value="">Any subject</option>
              {allSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Class / Grade</label>
            <select
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
              data-testid="search-grade-select"
            >
              <option value="">Any class</option>
              {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>Location</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Bangalore, Mumbai…"
              className="w-full rounded-xl border border-black/10 px-3 py-2 mt-1 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
              data-testid="search-location-input"
            />
          </div>
          <div className="lg:col-span-2 flex items-end gap-2">
            <button onClick={search} className="btn-yellow text-sm px-5 py-2 flex-1" data-testid="search-submit-btn">
              <SearchIcon className="h-4 w-4 inline mr-1" /> Search
            </button>
            <button onClick={reset} className="text-sm px-4 py-2 rounded-full border border-black/10" style={{ color: "#13A699" }} data-testid="search-reset-btn">
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <TeacherCardSkeleton key={`sk-${i}`} />)}
          </div>
        ) : !searched ? (
          <div className="bg-white rounded-3xl p-8 text-center border border-black/5">
            <p className="text-sm" style={{ color: "#13A699" }}>Enter filters above and click Search.</p>
          </div>
        ) : results.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 text-center border border-black/5">
            <p className="text-sm" style={{ color: "#13A699" }}>No teachers match your filters. Try widening your search.</p>
          </div>
        ) : (
          <>
            <div className="text-xs mb-3 uppercase font-bold" style={{ color: "#13A699" }}>
              {results.length} mentor{results.length === 1 ? "" : "s"} found
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {results.map((t) => <TeacherCard key={t.id} teacher={t} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============ BROWSE TAB ============
function BrowseTab() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState("");

  const load = async (loc = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (loc) params.set("location", loc);
      const { data } = await api.get(`/teachers/browse?${params.toString()}`);
      setTeachers(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="parent-browse-tab">
      <div className="bg-white rounded-3xl p-4 border border-black/5 flex items-center gap-2 flex-wrap">
        <SearchIcon className="h-4 w-4" style={{ color: "#13A699" }} />
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Filter by location…"
          className="flex-1 min-w-[200px] rounded-xl border border-black/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699] text-sm"
          data-testid="browse-location-input"
        />
        <button onClick={() => load(location)} className="btn-yellow text-sm px-4 py-2" data-testid="browse-apply-btn">
          Apply
        </button>
      </div>
      <div className="mt-5">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <TeacherCardSkeleton key={`sk-${i}`} />)}
          </div>
        ) : teachers.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 text-center border border-black/5">
            <p className="text-sm" style={{ color: "#13A699" }}>No approved teachers available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {teachers.map((t) => <TeacherCard key={t.id} teacher={t} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ COURSES DONE TAB ============
function CoursesTab({ onPunchIn }) {
  const [bookings, setBookings] = useState([]);
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState({});
  const [comment, setComment] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [b, d] = await Promise.all([api.get("/bookings/me"), api.get("/demo/me")]);
      setBookings(b.data || []);
      setDemos(d.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitRating = async (b) => {
    const r = rating[b.id] || 5;
    const c = comment[b.id] || "";
    try {
      await api.post("/ratings", { booking_id: b.id, rating: r, comment: c });
      toast.success("Thanks for rating!");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <p className="text-sm" style={{ color: "#13A699" }}>Loading…</p>;

  const confirmedBookings = bookings.filter((b) => b.status === "confirmed");
  const pendingBookings = bookings.filter((b) => b.status === "pending_teacher_approval");
  const declinedBookings = bookings.filter((b) => b.status === "declined");
  const closedBookings = bookings.filter((b) => b.status === "completed" || b.status === "cancelled");

  return (
    <div data-testid="parent-courses-tab" className="space-y-6">
      <ParentCoursesPanel onChange={load} onPunchIn={onPunchIn} />
      {/* Demo bookings */}
      <div className="bg-white rounded-3xl p-5 border border-black/5">
        <h2 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }}>Demo Classes</h2>
        {demos.length === 0 ? (
          <p className="text-sm mt-1" style={{ color: "#13A699AA" }}>No demo classes yet.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {demos.map((d) => (
              <li key={d.id} className="rounded-xl border border-black/5 p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`courses-demo-${d.id}`}>
                <Link to={`/teacher/${d.teacher_id}`} className="text-sm hover:underline" style={{ color: "#13A699" }}>
                  <div className="font-bold">{d.other_name}</div>
                  <div className="text-xs">Paid ₹{d.amount} · {(d.paid_at || "").slice(0, 10)}</div>
                  {d.cancel_reason && (
                    <div className="text-xs italic mt-0.5" style={{ color: "#DC2626" }}>Cancel reason: {d.cancel_reason}</div>
                  )}
                </Link>
                <div className="flex items-center gap-2 flex-wrap">
                  {d.status === "cancelled" ? (
                    <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#DC2626", color: "#FFF" }}>
                      Cancelled
                    </span>
                  ) : (
                    <>
                      <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#FFD708", color: "#13A699" }}>Demo</span>
                      <DemoCancelButton demo={d} onChange={load} />
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pending */}
      {pendingBookings.length > 0 && (
        <div className="bg-white rounded-3xl p-5 border border-black/5">
          <h2 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }}>
            Awaiting Approval ({pendingBookings.length})
          </h2>
          <ul className="mt-3 space-y-2">
            {pendingBookings.map((b) => (
              <li key={b.id} className="rounded-xl border border-black/5 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`courses-pending-${b.id}`}>
                <Link to={`/teacher/${b.teacher_id}`} className="text-sm hover:underline" style={{ color: "#13A699" }}>
                  <div className="font-bold">{b.teacher_name}</div>
                  <div className="text-xs">{b.date} · {b.start_time}–{b.end_time}</div>
                </Link>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#FFD708", color: "#13A699" }}>
                    Pending
                  </span>
                  <CancelButton booking={b} role="parent" onChange={load} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Confirmed / Courses Done */}
      <div className="bg-white rounded-3xl p-5 border border-black/5">
        <h2 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }}>
          Confirmed Sessions ({confirmedBookings.length})
        </h2>
        {confirmedBookings.length === 0 ? (
          <p className="text-sm mt-1" style={{ color: "#13A699AA" }}>No confirmed sessions yet.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {confirmedBookings.map((b) => {
              const canRate = !b.rated;
              return (
                <li key={b.id} className="rounded-xl border border-black/5 p-3" data-testid={`courses-confirmed-${b.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Link to={`/teacher/${b.teacher_id}`} className="text-sm hover:underline" style={{ color: "#13A699" }}>
                      <div className="font-display font-bold text-base">{b.teacher_name}</div>
                      <div className="text-xs">{b.date} · {b.start_time}–{b.end_time}</div>
                      {b.child && (
                        <div className="text-xs mt-0.5">
                          For: <b>{b.child.name}</b>{b.child.grade ? ` · ${b.child.grade}` : ""}
                        </div>
                      )}
                      {(b.teacher_phone || b.teacher_location) && (
                        <div className="text-xs mt-1 px-2 py-1 rounded-lg inline-block" style={{ background: "#FFF7ED", color: "#13A699" }} data-testid={`mentor-contact-${b.id}`}>
                          {b.teacher_phone && <>📞 {b.teacher_phone}</>}
                          {b.teacher_phone && b.teacher_location && " · "}
                          {b.teacher_location && <>📍 {b.teacher_location}</>}
                        </div>
                      )}
                    </Link>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StartSessionButton booking={b} role="parent" onChange={load} />
                      <PunchInButton booking={b} role="parent" onChange={load} />
                      <ReportSessionButton booking={b} role="parent" onChange={load} />
                      <CancelButton booking={b} role="parent" onChange={load} />
                    </div>
                  </div>
                  {canRate ? (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          onClick={() => setRating({ ...rating, [b.id]: n })}
                          data-testid={`rating-${b.id}-${n}`}
                        >
                          <Star className="h-5 w-5" fill={(rating[b.id] || 0) >= n ? "#FFD708" : "transparent"} stroke="#13A699" />
                        </button>
                      ))}
                      <input
                        type="text"
                        placeholder="Optional comment"
                        value={comment[b.id] || ""}
                        onChange={(e) => setComment({ ...comment, [b.id]: e.target.value })}
                        className="rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699] w-full sm:w-56"
                        data-testid={`comment-${b.id}`}
                      />
                      <button onClick={() => submitRating(b)} className="btn-yellow text-xs px-3 py-2" data-testid={`submit-rating-${b.id}`}>
                        Submit Rating
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#FFD708", color: "#13A699" }}>Rated</span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Closed (cancelled / completed) */}
      {closedBookings.length > 0 && (
        <div className="bg-white rounded-3xl p-5 border border-black/5">
          <h2 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }}>
            History ({closedBookings.length})
          </h2>
          <ul className="mt-3 space-y-2">
            {closedBookings.map((b) => (
              <li key={b.id} className="rounded-xl border border-black/5 p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`courses-closed-${b.id}`}>
                <div>
                  <div className="font-bold text-sm" style={{ color: "#13A699" }}>{b.teacher_name}</div>
                  <div className="text-xs" style={{ color: "#13A699AA" }}>
                    {b.date} · {b.start_time}–{b.end_time}
                  </div>
                  {b.cancel_reason && (
                    <div className="text-xs italic mt-0.5" style={{ color: "#13A69999" }}>Reason: {b.cancel_reason}</div>
                  )}
                </div>
                <span
                  className="text-[10px] font-bold uppercase px-2 py-1 rounded-full"
                  style={{
                    background: b.status === "completed" ? "#13A699" : "#DC2626",
                    color: "#FFF7ED",
                  }}
                >
                  {b.status_label || b.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Declined */}
      {declinedBookings.length > 0 && (
        <div className="bg-white rounded-3xl p-5 border border-black/5">
          <h2 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }}>Declined ({declinedBookings.length})</h2>
          <ul className="mt-3 space-y-2">
            {declinedBookings.map((b) => (
              <li key={b.id} className="rounded-xl border border-black/5 p-3" data-testid={`courses-declined-${b.id}`}>
                <Link to={`/teacher/${b.teacher_id}`} className="text-sm hover:underline" style={{ color: "#13A699" }}>
                  <div className="font-bold">{b.teacher_name}</div>
                  <div className="text-xs">{b.date} · {b.start_time}–{b.end_time}</div>
                </Link>
                {b.teacher_response_note && (
                  <div className="text-xs italic mt-1" style={{ color: "#DC2626" }}>Mentor's note: {b.teacher_response_note}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============ COMPLETED SESSIONS TAB ============
function CompletedTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bookings/me");
        setBookings((data || []).filter((b) => b.status === "completed"));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="text-sm" style={{ color: "#13A699" }}>Loading…</p>;

  return (
    <div data-testid="parent-completed-tab">
      <div className="bg-white rounded-3xl p-5 border border-black/5">
        <h2 className="font-display text-xl font-bold uppercase inline-flex items-center gap-2" style={{ color: "#13A699" }}>
          <CheckCircle2 className="h-5 w-5" /> Completed Sessions ({bookings.length})
        </h2>
        <p className="text-xs mt-1" style={{ color: "#13A69999" }}>
          Sessions where both you and the mentor punched in. Mentor payout for each session is in progress.
        </p>
      </div>
      {bookings.length === 0 ? (
        <div className="mt-4 bg-white rounded-3xl p-8 text-center border border-black/5" data-testid="completed-empty">
          <p className="text-sm" style={{ color: "#13A69999" }}>
            No completed sessions yet. After your next session, both sides punch in "Mark Complete" to close it.
          </p>
        </div>
      ) : (
        <ul className="mt-4 space-y-2">
          {bookings.map((b) => (
            <li
              key={b.id}
              className="bg-white rounded-2xl border border-black/5 p-3 flex items-center justify-between flex-wrap gap-2"
              data-testid={`completed-session-${b.id}`}
            >
              <div>
                <div className="font-bold text-sm" style={{ color: "#13A699" }}>{b.teacher_name}</div>
                <div className="text-xs" style={{ color: "#13A69999" }}>
                  {b.date} · {b.start_time}–{b.end_time}
                </div>
                {b.child && (
                  <div className="text-xs mt-0.5" style={{ color: "#13A69988" }}>
                    For: <b>{b.child.name}</b>{b.child.grade ? ` · ${b.child.grade}` : ""}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {b.rated ? (
                  <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#FFD708", color: "#13A699" }}>★ Rated</span>
                ) : (
                  <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#FFF7ED", color: "#13A699", border: "1px solid #13A69922" }}>Awaiting your rating</span>
                )}
                <span className="text-xs font-bold uppercase px-2 py-1 rounded-full" style={{ background: "#13A699", color: "#FFF7ED" }}>Completed</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============ DEMO TAB ============
function DemoTab() {
  return (
    <div data-testid="parent-demo-tab" className="bg-white rounded-3xl p-8 text-center border border-black/5">
      <Sparkles className="h-10 w-10 mx-auto mb-3" style={{ color: "#FFD708" }} />
      <h2 className="font-display text-2xl font-bold uppercase" style={{ color: "#13A699" }}>Book a Demo Class</h2>
      <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "#13A699" }}>
        Try a 30-minute demo session for just <b>₹99</b>. Browse approved mentors, pick one, and pay securely via Razorpay.
      </p>
      <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
        <Link to="/search" className="btn-yellow text-sm px-5 py-2.5 inline-flex items-center gap-1" data-testid="demo-go-search-btn">
          <SearchIcon className="h-4 w-4" /> Go to Search
        </Link>
        <Link to="/parent/dashboard?tab=browse" className="text-sm px-5 py-2.5 rounded-full border border-[#13A699] inline-flex items-center gap-1" style={{ color: "#13A699" }} data-testid="demo-browse-btn">
          <Users className="h-4 w-4" /> Browse All Mentors
        </Link>
      </div>
    </div>
  );
}

// ============ MAIN ============
export default function ParentDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [tab, setTab] = useState(TABS.find((t) => t.key === tabParam)?.key || "profile");
  const [subjectsTree, setSubjectsTree] = useState(SUBJECTS_FALLBACK);
  const [pendingRating, setPendingRating] = useState(null);

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjectsTree(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    const checkPending = () => {
      api
        .get("/parent/enrollments/pending-rating")
        .then(({ data }) => { if (!cancelled) setPendingRating(data || null); })
        .catch(() => {});
    };
    checkPending();
    const id = setInterval(checkPending, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (tabParam && TABS.some((t) => t.key === tabParam) && tab !== tabParam) {
      setTab(tabParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  const switchTab = (k) => {
    setTab(k);
    setSearchParams({ tab: k }, { replace: true });
  };

  return (
    <div className="bg-peach min-h-screen" data-testid="parent-dashboard-page">
      <RatingBlockModal
        enrollment={pendingRating}
        onRated={() => setPendingRating(null)}
      />
      {pendingRating && (
        <div
          className="bg-amber-100 border-b-2 border-amber-300 text-amber-900 px-4 py-3 text-sm font-bold text-center"
          data-testid="rating-banner"
        >
          ⚠ Please rate your completed course "{pendingRating.course_title}" to continue.
        </div>
      )}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-14">
        <NotificationBanner />
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase" style={{ color: "#13A699" }}>
              Explorer Dashboard
            </h1>
            <p className="text-sm mt-1" style={{ color: "#13A699AA" }}>Welcome back, {user?.name}!</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex items-center gap-2 overflow-x-auto border-b border-black/10 pb-px">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => switchTab(t.key)}
                className={`text-sm font-bold px-4 py-2 rounded-t-xl inline-flex items-center gap-2 whitespace-nowrap transition ${
                  active ? "bg-white border border-b-white border-black/10" : "text-[#13A699]/60 hover:text-[#13A699]"
                }`}
                style={active ? { color: "#13A699" } : undefined}
                data-testid={`parent-tab-${t.key}`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-5">
          {tab === "profile" && <ProfileTab user={user} />}
          {tab === "subscription" && <SubscriptionPanel />}
          {tab === "wallet" && <WalletPanel role="parent" />}
          {tab === "search" && <SearchTab subjectsTree={subjectsTree} />}
          {tab === "browse" && <BrowseTab />}
          {tab === "courses" && <CoursesTab onPunchIn={setPendingRating} />}
          {tab === "completed" && <CompletedTab />}
          {tab === "demo" && <DemoTab />}
        </div>
      </section>
    </div>
  );
}
